import { createFileRoute } from "@tanstack/react-router";
import { DeviceDetail } from "@/components/devices/device-detail";
import { OwnerWorkspaceGate } from "@/components/owner-workspace-gate";
import { useDevices } from "@/lib/api/connections";

/**
 * A paired device: `/connectors/device/{id}`.
 *
 * Its own route rather than a `device:{id}` key on `$connectorKey`. The
 * prefixed-key trick exists so model providers and sandboxes can share the
 * single `$connectorKey` param without a provider named `slack` colliding with
 * the real Slack connector — a static segment has no such problem, and it
 * spares the URL a `%3A`. `device` is reserved as a connector key in
 * `@lobu/core/reserved` so the static segment can never shadow a real one.
 */
export const Route = createFileRoute("/$owner/connectors/device/$deviceId")({
	validateSearch: (
		search: Record<string, unknown>,
	): { connection?: number; settings?: boolean } => ({
		connection:
			typeof search.connection === "number"
				? search.connection
				: typeof search.connection === "string" &&
						/^\d+$/.test(search.connection)
					? Number(search.connection)
					: undefined,
		settings:
			search.settings === true || search.settings === "true" ? true : undefined,
	}),
	// Best-effort warm of the devices list; the page renders its own
	// loading / not-found states, so a failure here must not replace the page
	// chrome with the generic route-error UI.
	loader: ({ context: { queryClient } }) =>
		queryClient.prefetchQuery(useDevices.queryOptions()),
	component: DeviceDetailPage,
});

function DeviceDetailPage() {
	const { owner, deviceId } = Route.useParams();
	const { connection, settings } = Route.useSearch();
	return (
		<OwnerWorkspaceGate owner={owner}>
			{({ ownerSlug, organizationId }) => (
				<DeviceDetail
					ownerSlug={ownerSlug}
					deviceId={deviceId}
					organizationId={organizationId}
					selectedConnection={connection}
					openSettings={settings}
				/>
			)}
		</OwnerWorkspaceGate>
	);
}
